import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Form } from 'react-bootstrap';
import { LuSearch, LuEye, LuTruck, LuPackage } from 'react-icons/lu';
import moment from 'moment';
import { getShipmentList } from '../../redux/reducers/shipmentSlice';
import ShipmentStatusBadge from './partials/ShipmentStatusBadge';

export default function ShipmentList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Auth context for business ID mapping
    const businessId = useSelector(state => state.auth.userDetail?.primaryBusinessId) || localStorage.getItem("activeBusinessId");

    const { shipmentList, loading, pagination } = useSelector(state => state.shipment);

    const [filters, setFilters] = useState({ status: 'all', deliveryType: 'all', search: '', page: 1 });

    useEffect(() => {
        if (businessId) {
            dispatch(getShipmentList({ businessId, filters }));
        }
    }, [dispatch, businessId, filters]);

    const handleSearch = (e) => {
        e.preventDefault();
        dispatch(getShipmentList({ businessId, filters }));
    };

    return (
        <div className="container-fluid my-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="pageTitle">
                    <h1>Shipments</h1>
                </div>
                {/* <div>
                    <h4 className="fw-bold text-dark mb-1">Shipments</h4>
                    <p className="text-muted mb-0 small">Manage internal dispatch and third-party tracking</p>
                </div> */}
            </div>

            <Card className="border-0 shadow-sm rounded-4 mb-4">
                <div className="p-4 border-bottom">
                    <form onSubmit={handleSearch} className="row g-3 align-items-center">
                        <div className="col-12 col-md-4">
                            <div className="position-relative">
                                <LuSearch className="position-absolute text-muted" style={{ top: "12px", left: "14px" }} size={16} />
                                <input
                                    type="text"
                                    className="form-control rounded-pill ps-5 bg-light border-0"
                                    placeholder="Search tracking code..."
                                    value={filters.search}
                                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="col-6 col-md-2">
                            <Form.Select
                                className="rounded-pill bg-light border-0"
                                value={filters.status}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            >
                                <option value="all">All Statuses</option>
                                <option value="PENDING">Pending</option>
                                <option value="PROCESSING">Processing</option>
                                <option value="DISPATCHED">Dispatched</option>
                                <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                                <option value="DELIVERED">Delivered</option>
                                <option value="FAILED">Failed</option>
                            </Form.Select>
                        </div>
                        <div className="col-6 col-md-2">
                            <Form.Select
                                className="rounded-pill bg-light border-0"
                                value={filters.deliveryType}
                                onChange={(e) => setFilters({ ...filters, deliveryType: e.target.value })}
                            >
                                <option value="all">All Modes</option>
                                <option value="self">Self Delivery</option>
                                <option value="third_party">Third Party</option>
                            </Form.Select>
                        </div>
                    </form>
                </div>

                <div className="table-responsive p-0">
                    <Table className="align-middle mb-0" hover>
                        <thead className="bg-light">
                            <tr>
                                <th className="ps-4 fw-semibold text-muted small text-uppercase py-3">Tracking</th>
                                <th className="fw-semibold text-muted small text-uppercase">Customer</th>
                                <th className="fw-semibold text-muted small text-uppercase">Subtotal</th>
                                <th className="fw-semibold text-muted small text-uppercase">Delivery Mode</th>
                                <th className="fw-semibold text-muted small text-uppercase">Status</th>
                                <th className="fw-semibold text-muted small text-uppercase">Created</th>
                                <th className="pe-4 text-end fw-semibold text-muted small text-uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-5 text-muted">Loading shipments...</td>
                                </tr>
                            ) : shipmentList?.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-5 text-muted">No shipments found.</td>
                                </tr>
                            ) : (
                                shipmentList.map(shipment => (
                                    <tr key={shipment._id}>
                                        <td className="ps-4">
                                            <span className="fw-bold text-dark">{shipment.trackingCode}</span>
                                            <div className="text-muted small">Inv #{shipment.invoiceId?._id?.slice(-8).toUpperCase()}</div>
                                        </td>
                                        <td>
                                            <div className="fw-semibold text-dark">{shipment.customerId?.name || 'Unknown'}</div>
                                            <div className="text-muted small">{shipment.deliveryAddress?.city}, {shipment.deliveryAddress?.country}</div>
                                        </td>
                                        <td>
                                            <span className="fw-semibold text-dark">${shipment.invoiceId?.totalAmount}</span>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                {shipment.deliveryType === 'self' ? <LuTruck className="text-primary" /> : <LuPackage className="text-primary" />}
                                                <span className="fw-semibold small">{shipment.deliveryType === 'self' ? 'Self Delivery' : 'Third Party'}</span>
                                            </div>
                                        </td>
                                        <td><ShipmentStatusBadge status={shipment.status} /></td>
                                        <td>
                                            <span className="small text-muted">{moment(shipment.createdAt).format('MMM D, YYYY')}</span>
                                        </td>
                                        <td className="pe-4 text-end">
                                            <button
                                                className="btn btn-light rounded-circle p-2 border"
                                                onClick={() => navigate(`/shipment/${shipment._id}`)}
                                                title="View Detail"
                                            >
                                                <LuEye size={16} className="text-muted" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>

                {/* Pagination */}
                {pagination?.pages > 1 && (
                    <div className="px-4 py-3 border-top d-flex justify-content-between align-items-center">
                        <span className="small text-muted">
                            Showing page {pagination.page} of {pagination.pages}
                        </span>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-sm btn-light rounded-pill border"
                                disabled={filters.page === 1}
                                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                            >
                                Previous
                            </button>
                            <button
                                className="btn btn-sm btn-light rounded-pill border"
                                disabled={filters.page === pagination.pages}
                                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}
